<template>
  <div v-if="tableData">
    <updated-list-component
      :table-headers="tableHeaders"
      :table-data="generateTableData"
      @open-offer-modal="setModalShowing($event, true)"
      @open-feedback-modal="onFeedbackModalOpen($event)"
    />
    <template v-if="clientData && selectedFeedbackData">
      <template v-if="!isMobile">
        <v-dialog
          v-model="displayFeedbackModal"
          :width="modal.xlarge_width"
          :max-width="modal.xlarge_maxWidth"
          persistent
        >
          <feedback-component
            :property-data="selectedProperty"
            :feedback-data="selectedFeedbackData"
            @on-feedback-updated="updateFeedback($event)"
            @close="closeFeedbackDetailsModal"
          />
        </v-dialog>
      </template>
      <template v-else>
        <feedback-component
          v-if="showMobileFeedbackModal"
          class="mobile-feedback-component"
          :property-data="selectedProperty"
          :feedback-data="selectedFeedbackData"
          @on-feedback-updated="updateFeedback($event)"
          @close="closeFeedbackDetailsModal"
        />
      </template>
    </template>
    <content-with-button-modal
      :modal-params="modalParams"
      :show-modal="displayPrefModal"
      @close-modal="closePrefModal($event)"
    />
    <modal-make-offer
      ref="modalMakeOffer"
      :home="selectedHome"
      :agent="associatedAgent || {}"
      @submit-modal="sendOfferMessage($event)"
    />
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import ModalMakeOffer from '../modals/ModalMakeOffer.vue'
import UpdatedListComponent from '../../uiUtils/UpdatedListComponent.vue'
import ContentWithButtonModal from '../../modals/ContentWithButtonModal.vue'
import FeedbackComponent from '../../feedback/FeedbackComponent'
import refreshClientData from '../../mixins/refreshClientData'
import {
  calculateFeedbackMatch,
  getPropertyKeyValue,
  isOfferMade
} from '../../../utils/propertyUtils'
import { sendOfferMessage, setOfferTimestamp } from '../../../services/messagesService'
import { getPropertyById } from '../../../services/propertiesService'
import { isEmpty } from '../../../utils/generalUtils'
import colors from '../../../scss/_colors.scss'

export default {
  components: {
    ModalMakeOffer,
    UpdatedListComponent,
    ContentWithButtonModal,
    FeedbackComponent
  },

  mixins: [refreshClientData],

  props: {
    tableHeaders: {
      type: Array,
      required: true
    },
    tableData: {
      type: Array,
      required: true
    },
    feedbackType: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      maxVisible: 6,
      hoverRow: null,
      displayFeedbackModal: false,
      selectedFeedbackData: null,
      displayPropertyModal: false,
      selectedProperty: null,
      selectedHome: {},
      displayPrefModal: false,
      modalParams: {},
      colors
    }
  },

  computed: {
    ...mapState('client', [
      'clientData',
      'clientFavorites',
      'associatedAgent',
      'clientProvideFeedback'
    ]),
    ...mapState('common', ['isMobile', 'showMobileFeedbackModal']),
    ...mapState('style', ['modal']),
    generateTableData() {
      if (this.isMobile) {
        return this.generateMobileFeedbackData(this.tableData)
      }
      return this.generateFeedbackData(this.tableData)
    }
  },

  mounted() {
    if (this.clientProvideFeedback) {
      const item = this.tableData.find(
        data => data.propertyId === this.clientProvideFeedback
      )
      this.onFeedbackModalOpen(item)
    }
  },

  destroyed() {
    this.setClientProvideFeedback(null)
    this.setShowingMobileBackButton(false)
    this.closeFeedbackDetailsModal()
  },

  methods: {
    ...mapMutations('client', {
      setClientProvideFeedback: 'SET_CLIENT_PROVIDE_FEEDBACK'
    }),
    ...mapMutations('common', {
      setSelectedHome: 'SET_SELECTED_HOME',
      setPersonName: 'SET_PERSON_NAME',
      setShowMobileFeedbackModal: 'SET_SHOW_MOBILE_FEEDBACK_MODAL'
    }),
    ...mapMutations('mobile', {
      setShowingMobileBackButton: 'SET_SHOWING_MOBILE_BACK_BUTTON'
    }),

    generateFeedbackData(tableDataArray) {
      let responseData = []
      tableDataArray.sort(this.compare)

      tableDataArray.map((item, index) => {
        let rowObject = {}
        rowObject[0] = {
          type: 'addressImg',
          data: item
        }

        const picStatus = item.feedback && !isEmpty(item.feedback.media)
        rowObject[1] = { type: 'date', text: item._timestamp }
        rowObject[2] = { type: 'picIndicator', picStatus: picStatus }

        // Will be using full client property object for feedback store values
        // through the app in feedback modal instead of property homes. -- TODO
        const propertyObject = this.clientFavorites.find(
          home => home._id === item.propertyId
        )
        rowObject[3] = isOfferMade(propertyObject)
          ? {
            type: 'text',
            text: 'OFFER REQUESTED',
            color: '#bb68cd'
          }
          : {
            type: 'clickable',
            text: 'MAKE OFFER',
            color: '#bb68cd',
            data: { value: item, type: 'offer', action: 'open-offer-modal' }
          }

        if (this.feedbackType === 'provided') {
          rowObject[4] = {
            type: 'clickable',
            text: this.calculateFeedbackPercent(item),
            data: { value: item, type: 'match', action: 'open-feedback-modal' }
          }
        } else {
          rowObject[4] = {
            type: 'clickableText',
            text: 'PROVIDE',
            class: 'scheduled',
            color: colors.hwayzBrandGold,
            data: {
              value: item,
              type: 'feedback',
              action: 'open-feedback-modal'
            }
          }
        }
        responseData.push(rowObject)
      })
      return responseData
    },

    generateMobileFeedbackData(tableDataArray) {
      let responseData = []
      tableDataArray.sort(this.compare)

      tableDataArray.map((item, index) => {
        let rowObject = {}
        rowObject[0] = {
          type: 'addressImg',
          data: item
        }

        if (this.feedbackType === 'provided') {
          rowObject[1] = {
            type: 'clickable',
            text: this.calculateFeedbackPercent(item),
            data: { value: item, type: 'match', action: 'open-feedback-modal' }
          }
        } else {
          rowObject[1] = {
            type: 'clickable',
            text: 'Provide Feedback',
            class: 'scheduled',
            data: {
              value: item,
              type: 'feedback',
              action: 'open-feedback-modal'
            }
          }
        }
        responseData.push(rowObject)
      })
      return responseData
    },

    // getMakeOfferText(item) {
    //   if (item.makeOfferRequested && dateOfLastFeedback(item.makeOfferRequested)) {
    //     return {
    //       type: 'clickable',
    //       text: 'OFFER SENT',
    //       color: '#bb68cd'
    //     }
    //   }
    //   return {
    //     type: 'clickable',
    //     text: 'MAKE OFFER',
    //     color: '#bb68cd',
    //     data: { value: item, type: 'offer', action: 'open-offer-modal' }
    //   }
    // },

    updateFeedback(feedback) {
      this.selectedFeedbackData = feedback
    },

    async onFeedbackModalOpen(data) {
      this.selectedProperty = (await getPropertyById(data.propertyId)).data
      if (this.isMobile) {
        this.selectedFeedbackData = data
        this.setShowMobileFeedbackModal(true)
        this.setShowingMobileBackButton(true)
        this.setPersonName(this.clientData.name)
        this.setSelectedHome(data)
      } else {
        if (this.clientData && this.clientData.homePrefs) {
          this.displayFeedbackModal = true
          this.selectedFeedbackData = data
        } else {
          Object.assign(this.modalParams, {
            title: 'Please set your needs and wants preferences in your account before filling out feedback for a home',
            btnText: 'Redirect to Account',
            type: 'redirect'
          })
          this.displayPrefModal = true
        }
      }
    },

    compare(a, b) {
      if (
        parseInt(this.calculateFeedbackPercent(a)) >
        parseInt(this.calculateFeedbackPercent(b))
      ) {
        return -1
      } else {
        return 1
      }
    },

    closeFeedbackDetailsModal(data) {
      this.setShowMobileFeedbackModal(false)
      this.setShowingMobileBackButton(false)
      this.setSelectedHome(null)
      this.displayFeedbackModal = false
      this.selectedFeedbackData = null
    },

    closePrefModal(type = null) {
      this.displayPrefModal = false
      if (type === 'redirect') {
        this.closeFeedbackDetailsModal()
        this.$router.push({ name: 'client-account' })
      } else if (type === 'offer') {
        this.closeFeedbackDetailsModal()
      }
    },

    setModalShowing(data, status) {
      if (data.feedback.match) {
        this.selectedHome = status
          ? getPropertyKeyValue(data.propertyId, this.clientFavorites)
          : ''
        this.$refs.modalMakeOffer.setModalShowing(status)
      } else {
        Object.assign(this.modalParams, {
          title: 'Please provide feedback before making an offer',
          btnText: 'Close',
          type: 'offer'
        })
        this.displayPrefModal = true
      }
    },

    async sendOfferMessage(data) {
      await sendOfferMessage(data)
      const response = await setOfferTimestamp(this.clientData._id, data.favoriteId)
      this.$refs.modalMakeOffer.setModalShowing(false)
      this.$emit('offer-message-sent', response.data)
    },

    calculateFeedbackPercent(home) {
      if (home.feedback) {
        return calculateFeedbackMatch(home.feedback.match)
      }
      return '9999.99'
    }
  }
}
</script>

<style lang="sass" scoped>
#feedbackTableData
  background-color: $hwayzWhite
  box-sizing: border-box
  font-size: $hwayzFont1rem4
  height: 60vh

.column-location
  padding: 1rem !important

.img-wrap
  cursor: pointer
  position: relative
  width: 25%
  min-width: 10rem
  height: 100%
  display: inline-block
  vertical-align: middle
  margin-right: 1rem

.img-text
  position: absolute
  top: 0
  left: 0
  height: 100%
  width: 100%
  border-radius: 0.5rem
  background-color: rgba($black, 0.5)
  color: $hwayzWhite
  text-align: center

.home-image
  border-radius: 0.5rem

a
  text-transform: uppercase
  font-size: $hwayzFont1rem2

.hyperlink-feedback
  color: $hwayzTeal !important

.label-feedback-percentage
  text-transform: uppercase
  font-size: 1.2rem
  cursor: pointer

.hyperlink-offer
  color: $hwayzPink !important

.indicator-pictures
  width: 1.2rem
  height: 1.2rem
  border-radius: 50%
  margin: 0 auto

  border: 0.1rem solid $hwayzOrange
  background-color: white

.indicator-pictures--true
  border: 0.1rem solid $hwayzDarkenGreen
  background-color: $hwayzDarkenGreen

/* Scrollbar style */
tbody::-webkit-scrollbar-track
  background-color: $hwayzce
  /* make the track seem narrower than the thumb */
  border: solid 1px white

tbody::-webkit-scrollbar
  width: 4px
  background-color: $hwayzce

tbody::-webkit-scrollbar-thumb
  background-color: red
</style>
